import { Component, Inject, LOCALE_ID, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { ConfigService } from '../@core/services/theme/config.service';
import { Settings } from 'luxon';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { NavigationService } from '../@core/services/theme/navigation.service';
import { LayoutService } from '../@core/services/theme/layout.service';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { SplashScreenService } from '../@core/services/theme/splash-screen.service';
import { Style, StyleService } from '../@core/services/theme/style.service';
import { ConfigName } from '../@core/interfaces/theme/config-name.model';
import { permissions, entityTypes, MY_LEADS_ROUTE } from './shared/helpers/roles-permissions';
import { CheckoutComponent } from './modules/employment/employment/checkout/checkout.component';
import { MatDialog } from '@angular/material';
import { InvoicePayComponent } from './modules/employment/invoice-pay/invoice-pay.component';
import { environment } from 'src/environments/environment';
import { ThemeService } from 'src/@core/services/theme.service';
import { AppService } from 'src/@core/services/app.service';
import { AryaChatService } from 'src/@core/services/assistant/arya-chat.service';
import { AssistantService } from 'src/@core/services/assistant/assistant.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'hour4u admin';
  message;
  private subs: Subscription[] = [];

  constructor(private configService: ConfigService,
    private styleService: StyleService,
    private renderer: Renderer2,
    private platform: Platform,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) private localeId: string,
    private layoutService: LayoutService,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private splashScreenService: SplashScreenService,
    private router: Router,
    private dialog: MatDialog,
    public themeService: ThemeService,
    public aryaChatService: AryaChatService,
    private assistantService: AssistantService,
    private appService: AppService
  ) {

    this.themeService.loadTheme();
    let imageUrl = "";
    if (this.themeService.isDarkTheme) {
      imageUrl = "assets/house-of-agents.svg";
    } else {
      imageUrl = "assets/logo.svg";
    }
    this.configService.updateConfig({
      sidenav: {
        imageUrl: imageUrl
      }
    });

    router.events
      .subscribe((event: NavigationStart) => {
        if (event.navigationTrigger === 'popstate') {
          // Perform actions
          console.log("++++++++++++++++++", event)
        }
      });
    Settings.defaultLocale = this.localeId;

    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }

    /**
     * Customize the template to your needs with the ConfigService
     * Example:
     *  this.configService.updateConfig({
     *    sidenav: {
     *      title: 'Custom App',
     *      imageUrl: '//placehold.it/100x100',
     *      showCollapsePin: false
     *    },
     *    showConfigButton: false,
     *    footer: {
     *      visible: false
     *    }
     *  });
     */

    /**
     * Config Related Subscriptions
     * You can remove this if you don't need the functionality of being able to enable specific configs with queryParams
     * Example: example.com/?layout=apollo&style=default
     */
    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('rtl') && coerceBooleanProperty(queryParamMap.get('rtl')))
    ).subscribe(queryParamMap => {
      this.document.body.dir = 'rtl';
      this.configService.updateConfig({
        rtl: true
      });
    });

    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('layout'))
    ).subscribe(queryParamMap => this.configService.setConfig(queryParamMap.get('layout') as ConfigName));

    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('style'))
    ).subscribe(queryParamMap => this.styleService.setStyle(queryParamMap.get('style') as Style));


    const betaBadge = {
      value: 'Beta',
      background: '#FCF5F0',
      color: '#A83126',
    };

    this.navigationService.items = [
      {
        type: 'link',
        label: 'Home',
        route: '/dashboard',
        icon: 'assets/home.svg',
        iconActive: 'assets/home-active.svg',
        permissions: [permissions.DASHBOARD_VIEW]
      },
      // {
      //   type: 'link',
      //   label: 'Work order',
      //   route: '/enterprise-admin/all/all/',
      //   icon: 'assets/sidebar/workorder.png',
      //   permissions: [permissions.WORKORDER_VIEW]
      // },
      // {
      //   type: 'link',
      //   label: 'Venues',
      //   route: '/venues',
      //   icon: 'assets/sidebar/venues.png',
      //   permissions: [permissions.VENUES_VIEW]
      // },
      // {
      //   type: 'link',
      //   label: 'Bookings',
      //   route: '/bookings',
      //   icon: 'assets/sidebar/booking.png',
      //   permissions: [permissions.BOOKINGS_VIEW]
      // },
      {
        // Whoever holds MY_LEADS_VIEW sees this, whatever their role is called.
        // sidenav-item keys the "only work assigned leads" blanket-hide off the
        // same permission, so this entry survives it.
        type: 'link',
        label: 'My Leads',
        route: MY_LEADS_ROUTE,
        icon: 'assets/assigned-leads.svg',
        iconActive: 'assets/assigned-leads-active.svg',
        permissions: [permissions.MY_LEADS_VIEW]
      },
      {
        type: 'link',
        label: 'Projects',
        route: '/job',
        routerLinkActive: { exact: true },
        icon: 'assets/project-box.svg',
        iconActive: 'assets/project-box-active.svg',
        permissions: [permissions.PROJECT_VIEW]
      },
      // {
      //   type: 'link',
      //   label: 'Arya',
      //   route: '/arya',
      //   icon: 'assets/arya-icon.svg',
      //   iconActive: 'assets/arya-icon-active.svg',
      //   permissions: [permissions.PROJECT_VIEW],
      //   badge: betaBadge,
      // },
      {
        type: 'link',
        label: 'Tasks',
        route: '/job/tasks',
        icon: 'assets/tasks-icon.svg',
        iconActive: 'assets/tasks-icon-active.svg',
        permissions: [permissions.PROJECT_VIEW],
        badge: betaBadge,
      },
      {
        type: 'link',
        label: 'Outcomes',
        route: '/job/outcomes',
        icon: 'assets/outcomes-icon.svg',
        iconActive: 'assets/outcomes-icon-active.svg',
        permissions: [permissions.PROJECT_VIEW],
        badge: betaBadge,
      },
      {
        type: 'link',
        label: 'Reports',
        route: '/reports',
        icon: 'assets/reports-icon.svg',
        iconActive: 'assets/reports-icon-active.svg',
        permissions: [permissions.PROJECT_VIEW]
      },
      // {
      //   type: 'link',
      //   label: 'Agents',
      //   route: '/agents',
      //   icon: 'assets/sidebar/tag-user.svg',
      //   permissions: [permissions.AGENTS_VIEW]
      // },
      {
        type: 'link',
        label: 'Credits',
        // route: '/credits',
        route: '/credit-usage/credit-list',
        icon: 'assets/coin.svg',
        iconActive: 'assets/coin-active.svg',
        permissions: [permissions.CREDITS_VIEW]
      },
      {
        type: 'link',
        label: 'Members & Roles',
        route: '/roles-permissions',
        icon: 'assets/sidebar/team-members.png',
        permissions: [permissions.MEMBERS_AND_ROLES_VIEW]
      },
      // {
      //   type: 'link',
      //   label: 'Referrals',
      //   route: '/referrals/referrals-list',
      //   icon: 'assets/sidebar/clients.png',
      //   permissions: [permissions.REFERRAL_VIEW]
      // },
      // {
      //   type: 'link',
      //   label: 'Usage',
      //   route: '/credit-usage',
      //   icon: 'assets/sidebar/usages.svg',
      //   permissions: [permissions.CREDITS_VIEW]
      // },
      // {
      //   type: 'link',
      //   label: 'Video agent',
      //   route: '/video-agent',
      //   icon: 'assets/sidebar/projects.png',
      //   permissions: [permissions.PROJECT_VIEW]
      // },
      // {
      //   type: 'link',
      //   label: 'Subscriptions',
      //   route: '/subscription-plans',
      //   icon: 'assets/sidebar/clients.png',
      //   permissions: [permissions.SUBSCRIPTIONS_VIEW]
      // },
      {
        type: 'dropdown',
        label: 'Users',
        icon: 'assets/sidebar/users.png',
        permissions: [permissions.USERS_VIEW],
        children: [
          {
            type: 'link',
            label: 'Agencies',
            route: '/employer',
            icon: 'assets/sidebar/users.png',
            permissions: [permissions.AGENCIES_VIEW],
          },
          {
            type: 'link',
            label: 'Team Members',
            route: '/enterprise-admin/team-member',
            icon: 'assets/sidebar/users.png',
            permissions: [permissions.TEAM_MEMBERS_VIEW],
          },
          {
            type: 'link',
            label: 'Vendors',
            route: '/enterprise-admin/vendors',
            icon: 'assets/market.svg',
            permissions: [permissions.VENDORS_VIEW],
          },
          {
            type: 'link',
            label: 'Champs',
            route: '/enterprise-admin/champ-user',
            icon: 'assets/sidebar/champ.png',
            permissions: [permissions.CHAMPS_VIEW],
          },
          {
            type: 'link',
            label: 'Clients',
            route: '/enterprise-admin/enterprise-admins',
            icon: 'assets/sidebar/clients.png',
            permissions: [permissions.ENTERPRISES_VIEW],
          },
          {
            type: 'link',
            label: 'Permission Users',
            route: '/enterprise-admin/permission-users',
            icon: 'assets/sidebar/users.png',
            permissions: [permissions.PERMISSION_USERS_VIEW],
          }
        ]
      },
      {
        type: 'link',
        label: 'Digital Portfolio',
        route: '/digital-portfolio',
        icon: 'assets/sidebar/dashboard.png',
        permissions: [permissions.DIGITAL_PORTFOLIO_VIEW]
      },
      {
        type: 'link',
        label: 'MarketPlace',
        route: '/marketplace',
        icon: 'assets/market.svg',
        permissions: [permissions.DIGITAL_PORTFOLIO_VIEW]
      },
      {
        type: 'link',
        label: 'Service Bookings',
        route: '/service-bookings',
        icon: 'assets/sidebar/booking.png',
        permissions: [permissions.DIGITAL_PORTFOLIO_VIEW]
      },
      {
        type: 'link',
        label: 'Settings',
        route: '/settings',
        icon: 'assets/sidebar/settings.png',
        permissions: [permissions.SETTINGS_VIEW]
      },
      // {
      //   type: 'link',
      //   label: 'Help Videos',
      //   route: '/help-videos',
      //   icon: 'assets/help.svg',
      //   permissions: [permissions.SETTINGS_VIEW],
      //   separatorBefore: true
      // }
    ];

    // Billing rate cards are HOA staff's, not a client's. Nav entries here are
    // filtered by permission, which every client admin also holds, so this one is
    // appended only for INTERNAL rather than declared in the list above.
    const account: any = this.appService.getCurrentAccount();
    const entityType = account && account.entity && account.entity.entityType;
    if (entityType === entityTypes.INTERNAL) {
      this.navigationService.items.push({
        type: 'link',
        label: 'Billing rates',
        route: '/billing/rates',
        icon: 'assets/coin.svg',
        iconActive: 'assets/coin-active.svg'
      } as any);
      this.navigationService.items.push({
        type: 'link',
        label: 'Call analysis debug',
        route: '/call-analysis-debug/logs',
        icon: 'assets/sidebar/settings.png'
      } as any);
    }

    const aryaItem = this.navigationService.items.find(
      i => i.type === 'link' && (i as any).label === 'Arya'
    ) as any;
    this.subs.push(this.assistantService.unread$.subscribe(count => {
      if (aryaItem) {
        aryaItem.badge = count > 0
          ? { value: String(count), background: '#e53e3e', color: '#fff' }
          : betaBadge;
      }
    }));
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  ngOnInit() {
    const currentVersion = environment.version; // Assuming this is already a number
    const storedVersionString = localStorage.getItem('appVersion');

    console.log("versions :", storedVersionString, currentVersion);

    const storedVersion = storedVersionString ? parseFloat(storedVersionString) : null;

    // Check if the stored version does not match the current version
    // if (storedVersion !== currentVersion) {
    //   // First, set the new app version to localStorage
    //   localStorage.setItem('appVersion', currentVersion.toString());

    //   // Clear cookies
    //   document.cookie.split(";").forEach((c) => {
    //     document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=Thu, 01 Jan 1970 00:00:00 GMT");
    //   });

    //   // Optionally, you could also clear local storage if needed, but do NOT clear the app version
    //   localStorage.clear();

    //   // Re-set the version after clearing local storage
    //   localStorage.setItem('appVersion', currentVersion.toString());

    //   // Redirect user to the login page or home page
    //   this.router.navigateByUrl('/');
    // }

    // Handle router logic for different URLs
    if (this.router.url.includes('checkout')) {
      this.route.params.subscribe(data => {
        this.copyEmployementAdvancePaymentLink(data);
      });
    }
    if (this.router.url.includes('invoice-pay')) {
      this.route.params.subscribe(data => {
        this.copyEmployementPayInvoiceLink(data);
      });
    }
  }



  // checkout modal open
  copyEmployementAdvancePaymentLink(employment: any) {
    const dialogRef = this.dialog.open(CheckoutComponent, {
      width: '700px',
      data: employment,
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(
      result => {
        console.log(result)
      }
    );
  }

  // share-invoice modal open
  copyEmployementPayInvoiceLink(employment: any) {
    const dialogRef = this.dialog.open(InvoicePayComponent, {
      width: '700px',
      data: employment,
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(
      result => {
        console.log(result)
      }
    );
  }

}
